// @ts-nocheck
'use client';

import React from 'react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { executorSchema, executorFormSchema } from '@/lib/schema';
import { useWillStore } from '@/lib/store';
import { Input } from '@/app/components/ui/Input';
import { Label } from '@/app/components/ui/Label';
import { Button } from '@/app/components/ui/Button';
import { Checkbox } from '@/app/components/ui/Checkbox';
import { Modal } from '@/app/components/ui/Modal';
import { StepWrapper } from '../StepWrapper';
import { FormNavigation } from '../FormNavigation';
import { AddressSection } from '../AddressSection';
import { AddressAutocomplete } from '../AddressAutocomplete';
import { Briefcase, Check, Pencil, Trash2, User, X } from 'lucide-react';
import { ActionCard } from '@/app/components/ui/ActionCard';

// Helper to check if name has both first and last name
const isFullName = (name: string | undefined): boolean => {
  if (!name || name.trim() === '') return false;
  return name.trim().indexOf(' ') !== -1;
};
import * as yup from 'yup';



const executorsStepSchema = yup.object({
  executors: yup.array().of(executorFormSchema).min(1, 'At least one executor is required').required(),
  substituteExecutors: yup.array().of(executorFormSchema).optional().default([]),
});

type ExecutorsData = yup.InferType<typeof executorsStepSchema>;

import { PersonForm } from '../shared/PersonForm';
import { QuickAddPeople } from '../shared/QuickAddPeople';
import { useSavedPeople, SAVED_PEOPLE_PRESETS } from '@/lib/hooks/useSavedPeople';
import { usePersonList } from '@/lib/hooks/usePersonList';

export const Executors = () => {
  const { data, setData, setStep, setStepBack, isEditingFromReview, setIsEditingFromReview, navigatingBack, clearNavigatingBack, addManuallyAddedPerson, updatePerson, removePersonEverywhere } = useWillStore();

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    getValues,
    trigger,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ExecutorsData>({
    resolver: yupResolver(executorsStepSchema) as any,
    mode: 'onBlur',
    defaultValues: {
      executors: data.executors?.length ? data.executors.map(e => ({ ...e, isOver18: true })) : [],
      substituteExecutors: data.substituteExecutors?.length ? data.substituteExecutors.map(e => ({ ...e, isOver18: true })) : [],
    },
  });

  // Sync form with store data when it changes (e.g. after login/rehydration)
  // Sync form with store data when it changes (e.g. after login/rehydration)
  React.useEffect(() => {
    if (data.executors || data.substituteExecutors) {
      reset({
        executors: data.executors?.length ? data.executors.map(e => ({ ...e, isOver18: true })) : [],
        substituteExecutors: data.substituteExecutors?.length ? data.substituteExecutors.map(e => ({ ...e, isOver18: true })) : [],
      });
    }
  }, [data.executors, data.substituteExecutors, reset]);

  const manuallyAddedPeople = data.manuallyAddedPeople || [];

  // Helper: determine if a named child is under 18 based on DD/MM/YYYY
  const isMinorChild = (fullName: string) => {
    const childRecord = data.children?.find((child: any) => child.fullName === fullName);
    if (!childRecord?.dateOfBirth) return false;
    const parts = childRecord.dateOfBirth.split('/');
    if (parts.length !== 3) return false;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    const dob = new Date(year, month, day);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age < 18;
  };

  // Track manually added executor (saves to global store)
  const trackManuallyAddedExecutor = (person: { fullName: string; relationship: string; address?: any }) => {
    if (!savedPeople.some(p => p.fullName === person.fullName) &&
      !manuallyAddedPeople.some((p: any) => p.fullName === person.fullName)) {
      addManuallyAddedPerson(person);
    }
  };

  const executorsList = usePersonList({
    control,
    name: 'executors',
    setValue,
    onPersonSaved: (data) => {
      if (data.fullName) {
        trackManuallyAddedExecutor({
          fullName: data.fullName,
          relationship: data.relationship || 'Executor',
          address: data.address
        });
      }
    }
  });

  const substitutesList = usePersonList({
    control,
    name: 'substituteExecutors',
    setValue,
    onPersonSaved: (data) => {
      if (data.fullName) {
        trackManuallyAddedExecutor({
          fullName: data.fullName,
          relationship: data.relationship || 'Executor',
          address: data.address
        });
      }
    }
  });

  const { fields } = executorsList;
  const { fields: subFields } = substitutesList;

  // Derive showSubstitute directly from data
  const showSubstitute = (data as any).showSubstituteChoice;

  // State for editing quick add people
  const [editingQuickAddPerson, setEditingQuickAddPerson] = React.useState<any>(null);
  const [tempQuickAddPerson, setTempQuickAddPerson] = React.useState<any>(null);
  const [manualQuickAddAddressMode, setManualQuickAddAddressMode] = React.useState(false);

  const subRelationships = useWatch({
    control,
    name: 'substituteExecutors',
  });

  const executors = useWatch({ control, name: 'executors' });

  // Use the shared hook to get saved people (adults only for executors)
  const savedPeople = useSavedPeople(SAVED_PEOPLE_PRESETS.executors);

  // Get all people for executor selection (saved people + manually added people from global store)
  // Excludes charities and people under 18 since only individuals over 18 can be executors
  const getAllExecutorPeople = () => {
    // Start with saved people but merge any updated fields from manuallyAddedPeople
    const allPeople = savedPeople.map((person: any) => {
      const manuallyAdded = manuallyAddedPeople.find((p: any) => p.fullName === person.fullName);
      if (manuallyAdded) {
        return {
          ...person,
          address: manuallyAdded.address || person.address,
          isOver18: manuallyAdded.isOver18 ?? person.isOver18
        };
      }
      return person;
    });

    // Add any manually-added people that aren't in the saved people list
    manuallyAddedPeople.forEach((person: any) => {
      if (person?.fullName && !allPeople.some(p => p.fullName === person.fullName)) {
        allPeople.push({
          fullName: person.fullName,
          relationship: person.relationship || 'Executor',
          address: person.address,
          isOver18: person.isOver18
        });
      }
    });

    // Filter out charities, people under 18, and the main person (About You)
    return allPeople.filter(person =>
      person.relationship !== 'Charity' &&
      person.isOver18 !== false &&
      !isMinorChild(person.fullName) &&
      person.fullName !== data.personalDetails?.fullName
    );
  };

  const [showSingleExecutorWarning, setShowSingleExecutorWarning] = React.useState(false);
  const [hasConfirmedSingle, setHasConfirmedSingle] = React.useState(false);
  const [isMainExecutorsConfirmed, setIsMainExecutorsConfirmed] = React.useState(false);
  const [substituteError, setSubstituteError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (executors && executors.length > 1) {
      setShowSingleExecutorWarning(false);
    }
  }, [executors]);

  // Handle navigating back from next section - go to last sub-step (substitute executors)
  React.useEffect(() => {
    if (navigatingBack) {
      setIsMainExecutorsConfirmed(true); // Show substitute executors section
      clearNavigatingBack();
    }
  }, [navigatingBack, clearNavigatingBack]);

  const onSubmit = (formData: ExecutorsData) => {
    // Stage 1: Main Executors Confirmation
    if (!isMainExecutorsConfirmed) {
      if (formData.executors.length === 0) {
        return; // Need at least one executor
      }
      if (formData.executors.length === 1 && !showSingleExecutorWarning) {
        setHasConfirmedSingle(false);
        setShowSingleExecutorWarning(true);
        return;
      }

      // Save executors to store now so they persist if the user refreshes or if reset() is called
      const cleanExecutors = formData.executors.map(({ isOver18, ...rest }) => rest);
      setData({
        executors: cleanExecutors
      });

      setIsMainExecutorsConfirmed(true);
      window.scrollTo(0, 0);
      return;
    }

    // Stage 2: Substitute Executors
    if (showSubstitute === null || showSubstitute === undefined) {
      setSubstituteError("Please select Yes or No");
      return;
    }
    setSubstituteError(null);

    const substitutes = showSubstitute === true ? formData.substituteExecutors : [];

    const cleanExecutors = formData.executors.map(({ isOver18, ...rest }) => rest);
    const cleanSubstitutes = substitutes?.map(({ isOver18, ...rest }) => rest) || [];

    setData({
      executors: cleanExecutors,
      substituteExecutors: cleanSubstitutes
    });

    if (isEditingFromReview) {
      setIsEditingFromReview(false);
      setStep(5); // Return to Review (which is step 5)
    } else {
      setStep(5); // Skip Gifts (step 4) and go straight to Review
    }
  };

  const handleConfirmSingle = () => {
    setShowSingleExecutorWarning(false);

    // Save executors to store
    const currentExecutors = getValues().executors;
    const cleanExecutors = currentExecutors.map(({ isOver18, ...rest }) => rest);
    setData({
      executors: cleanExecutors
    });

    setIsMainExecutorsConfirmed(true);
    window.scrollTo(0, 0);
  };

  // Helper functions that were removed but needed for QuickAddPeople
  // Track manually added executor (saves to global store) - Moved up

  // Toggle executor selection
  const toggleExecutor = (person: { fullName: string; relationship: string; address?: any }) => {
    if (isMinorChild(person.fullName)) {
      setError('executors', { type: 'manual', message: 'Executors must be over 18.' });
      return;
    }
    clearErrors('executors');

    // Use fields array for index lookup to ensure sync with useFieldArray
    const existingIndex = fields.findIndex(f => f.fullName === person.fullName);
    if (existingIndex >= 0) {
      executorsList.remove(existingIndex);
    } else {
      executorsList.append({
        fullName: person.fullName,
        relationship: person.relationship,
        address: person.address || { line1: '', line2: '', city: '', postcode: '' },
        isOver18: true
      });
    }
  };

  // Toggle substitute executor selection
  const toggleSubExecutor = (person: { fullName: string; relationship: string; address?: any }) => {
    if (isMinorChild(person.fullName)) {
      setError('substituteExecutors', { type: 'manual', message: 'Executors must be over 18.' });
      return;
    }
    clearErrors('substituteExecutors');

    // Use subFields array for index lookup to ensure sync with useFieldArray
    const existingIndex = subFields.findIndex(f => f.fullName === person.fullName);
    if (existingIndex >= 0) {
      substitutesList.remove(existingIndex);
    } else {
      substitutesList.append({
        fullName: person.fullName,
        relationship: person.relationship,
        address: person.address || { line1: '', line2: '', city: '', postcode: '' },
        isOver18: true
      });
    }
  };

  const openEditQuickAddModal = (person: any, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent toggling selection
    setEditingQuickAddPerson(person);
    setTempQuickAddPerson({
      fullName: person.fullName,
      relationship: person.relationship,
      address: person.address || { line1: '', line2: '', city: '', postcode: '' },
      email: person.email, // If available
      isOver18: true // Assume adults in quick add
    });
    setManualQuickAddAddressMode(false);
  };

  const handleCloseQuickAddModal = () => {
    setEditingQuickAddPerson(null);
    setTempQuickAddPerson(null);
  };

  const handleSaveQuickAddPerson = () => {
    if (editingQuickAddPerson && tempQuickAddPerson) {
      updatePerson(editingQuickAddPerson.fullName, {
        fullName: tempQuickAddPerson.fullName,
        relationship: tempQuickAddPerson.relationship,
        address: tempQuickAddPerson.address,
        email: tempQuickAddPerson.email
      });
      handleCloseQuickAddModal();
    }
  };

  const hideHeader = isMainExecutorsConfirmed;

  // Check if a person is already an executor
  const isExecutorSelected = (personName: string) => {
    return fields.some(f => f.fullName === personName);
  };

  // Get all people for substitute executor selection (saved + manually added people, excluding main executors)
  // Excludes charities and people under 18 since only individuals over 18 can be executors
  const getAllSubExecutorPeople = () => {
    // Start with saved people but merge any updated fields from manuallyAddedPeople
    const allPeople = savedPeople.map((person: any) => {
      const manuallyAdded = manuallyAddedPeople.find((p: any) => p.fullName === person.fullName);
      if (manuallyAdded) {
        return {
          ...person,
          address: manuallyAdded.address || person.address,
          isOver18: manuallyAdded.isOver18 ?? person.isOver18
        };
      }
      return person;
    });

    // Add any manually-added people that aren't in the list
    manuallyAddedPeople.forEach((person: any) => {
      if (person?.fullName && !allPeople.some(p => p.fullName === person.fullName)) {
        allPeople.push({
          fullName: person.fullName,
          relationship: person.relationship || 'Executor',
          address: person.address,
          isOver18: person.isOver18
        });
      }
    });

    // Filter out main executors, charities, people under 18, and the main person (About You)
    return allPeople.filter(person =>
      !executors?.some(ex => ex.fullName === person.fullName) &&
      person.relationship !== 'Charity' &&
      person.isOver18 !== false &&
      !isMinorChild(person.fullName) &&
      person.fullName !== data.personalDetails?.fullName
    );
  };

  // Check if a person is already a substitute executor
  const isSubExecutorSelected = (personName: string) => {
    return subFields.some(f => f.fullName === personName);
  };

  // Track manually added substitute executor (saves to global store)
  const trackManuallyAddedSubExecutor = (person: { fullName: string; relationship: string; address?: any }) => {
    if (!savedPeople.some(p => p.fullName === person.fullName) &&
      !manuallyAddedPeople.some((p: any) => p.fullName === person.fullName)) {
      addManuallyAddedPerson(person);
    }
  };

  // Legacy filtered versions for modals (only unselected people)
  const availableSavedPeople = savedPeople.filter(
    person => !executors?.some(ex => ex.fullName === person.fullName) && !isMinorChild(person.fullName)
  );

  const availableSavedPeopleForSubs = savedPeople.filter(
    person => !subRelationships?.some(ex => ex.fullName === person.fullName) &&
      !executors?.some(ex => ex.fullName === person.fullName) &&
      !isMinorChild(person.fullName)
  );

  return (
    <>
      <StepWrapper
        title={isMainExecutorsConfirmed ? "Substitute Executors" : "Who shall carry out your Will instructions?"}
        description={isMainExecutorsConfirmed ? (
          <>
            <p className="mb-4 text-xl">
              A substitute executor steps in if your main executor(s) are unable or unwilling to act when the time comes.
              This could happen if they pass away before you, become too ill, or simply decline the role.
            </p>
            <p className="text-xl">
              Appointing a substitute is optional but recommended. It helps avoid delays and ensures someone you trust is always available to manage your estate.
            </p>
          </>
        ) : (
          <>
            <p className="mb-2 text-lg">An executor is the person (or people) you trust to manage your estate when the time comes. They will:</p>
            <ul className="list-disc list-inside mb-3 space-y-1 text-lg">
              <li>Locate and secure your assets</li>
              <li>Pay any outstanding debts and taxes</li>
              <li>Distribute your estate to your beneficiaries as instructed in your Will</li>
            </ul>
            <p className="text-lg">We recommend appointing two or more executors to act together, with a backup in case one is unable to act. You can choose family members, friends, or even a professional (like a solicitor).</p>
          </>
        )}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Main Executors List */}
          {!showSingleExecutorWarning && !isMainExecutorsConfirmed && (
            <>
              <div className="w-full md:w-1/2 space-y-6">
                <QuickAddPeople
                  people={getAllExecutorPeople()}
                  heading="Who shall carry out your Will instructions?"
                  selectedPeople={fields as any}
                  onToggle={toggleExecutor}
                  onEdit={openEditQuickAddModal}
                  isSelected={isExecutorSelected}
                />

                {/* Selected Executors Count */}
                {fields.length > 0 && (
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-primary">
                      {fields.length} Executor{fields.length !== 1 ? 's' : ''} Selected
                    </p>
                  </div>
                )}

                {/* Hidden inputs to persist executor data */}
                {fields.map((field, index) => (
                  <div key={field.id} className="hidden">
                    <input type="hidden" {...register(`executors.${index}.fullName`)} />
                    <input type="hidden" {...register(`executors.${index}.relationship`)} />
                    <input type="hidden" {...register(`executors.${index}.isOver18`)} />
                  </div>
                ))}

                <ActionCard showPlusIcon onClick={executorsList.modal.openAdd} className="w-full">Add Executor</ActionCard>
              </div>

              <FormNavigation
                onBack={undefined}
                isValid={fields.length > 0 && executors?.every(e =>
                  e.relationship === 'Professional'
                    ? !!e.companyName
                    : (!!e.fullName && !!e.isOver18 && !!(e.address?.line1 && e.address?.city && e.address?.postcode))
                )}
              />
            </>
          )}

          {isMainExecutorsConfirmed && !showSingleExecutorWarning && (
            <div className="animate-fade-in space-y-10">
              <div className="w-full md:w-1/2 mt-4">
                <div className="space-y-6">
                  <Label className="text-base font-medium text-gray-700">
                    Would you like to appoint a substitute executor?
                  </Label>
                  <div className="flex gap-4 staggered-fade-in">
                    <ActionCard
                      selected={showSubstitute === true}
                      onClick={() => {
                        setSubstituteError(null);
                        setData({ showSubstituteChoice: true });
                      }}
                    >
                      Yes
                    </ActionCard>
                    <ActionCard
                      selected={showSubstitute === false}
                      onClick={() => {
                        setSubstituteError(null);
                        setData({ showSubstituteChoice: false, substituteExecutors: [] });
                      }}
                    >
                      No
                    </ActionCard>
                  </div>
                  {substituteError && (
                    <p className="text-destructive text-base font-medium mb-4 animate-fade-in bg-red-50 p-2 rounded border border-red-100">
                      {substituteError}
                    </p>
                  )}
                </div>

                {showSubstitute && (
                  <div className="space-y-6 pt-8 animate-fade-in">
                    <QuickAddPeople
                      people={getAllSubExecutorPeople()}
                      selectedPeople={subFields as any}
                      onToggle={toggleSubExecutor}
                      onEdit={openEditQuickAddModal}
                      isSelected={isSubExecutorSelected}
                      heading=""
                    />

                    {/* Hidden inputs for all substitute executors */}
                    {subFields.map((field, index) => (
                      <div key={field.id} className="hidden">
                        <input type="hidden" {...register(`substituteExecutors.${index}.fullName`)} />
                        <input type="hidden" {...register(`substituteExecutors.${index}.relationship`)} />
                        <input type="hidden" {...register(`substituteExecutors.${index}.isOver18`)} />
                      </div>
                    ))}

                    {/* Only show card display for executors added via modal (not in saved people list) */}
                    {(() => {
                      const savedPeopleNames = getAllSubExecutorPeople().map(p => p.fullName);
                      const modalAddedExecutors = subFields.map((field, index) => ({
                        field,
                        index,
                        executor: subRelationships?.[index]
                      })).filter(({ executor }) => {
                        const hasData = !!executor?.fullName || !!executor?.companyName;
                        const isFromSavedPeople = savedPeopleNames.includes(executor?.fullName);
                        return hasData && !isFromSavedPeople;
                      });

                      if (modalAddedExecutors.length === 0) return null;

                      return (
                        <div className="grid grid-cols-1 gap-3">
                          {modalAddedExecutors.map(({ field, index, executor }) => (
                            <div
                              key={field.id}
                              className="flex items-center gap-4 p-4 border-2 rounded-xl border-primary bg-primary/5"
                            >
                              <div className="h-6 w-6 rounded border-2 flex items-center justify-center shrink-0 border-primary bg-primary text-white">
                                <Check className="h-4 w-4" />
                              </div>
                              <div className="flex flex-col min-w-0 flex-1">
                                <span className="text-base font-semibold text-gray-900 truncate">
                                  {executor?.relationship === 'Professional' && executor?.companyName
                                    ? executor.companyName
                                    : executor?.fullName}
                                </span>
                                <span className="text-sm text-muted-foreground">
                                  {executor?.relationship === 'Professional'
                                    ? `Professional${executor?.fullName ? ` • ${executor.fullName}` : ''}`
                                    : 'Individual'}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => substitutesList.modal.openEdit(index, executor)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-primary"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => substitutesList.remove(index)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-destructive"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    <ActionCard showPlusIcon onClick={substitutesList.modal.openAdd} className="w-full">Add Substitute Executor</ActionCard>
                  </div>
                )}
              </div>

              <FormNavigation
                onBack={() => setIsMainExecutorsConfirmed(false)}
                isValid={
                  showSubstitute === false ||
                  (showSubstitute === true && subFields.length > 0 && subRelationships?.every(e =>
                    e.relationship === 'Professional'
                      ? !!e.companyName
                      : (!!e.fullName && !!e.isOver18 && !!(e.address?.line1 && e.address?.city && e.address?.postcode))
                  ))
                }
              />
            </div>
          )}

          {/* Executor Modal */}
          <Modal
            isOpen={executorsList.modal.isOpen}
            onClose={executorsList.modal.close}
            title={(executorsList.modal.data?.fullName || executorsList.modal.data?.companyName) ? 'Edit Executor' : 'Add Executor'}
          >
            {executorsList.modal.isOpen && executorsList.modal.data && (
              <PersonForm
                initialData={executorsList.modal.data}
                onSave={executorsList.modal.save}
                showProfessionalOption={true}
                showEmail={true}
                showAgeSelection={true}
                hideUnder18={true}
              />
            )}
          </Modal>

          {/* Substitute Executor Modal */}
          <Modal
            isOpen={substitutesList.modal.isOpen}
            onClose={substitutesList.modal.close}
            title={(substitutesList.modal.data?.fullName || substitutesList.modal.data?.companyName) ? 'Edit Substitute Executor' : 'Add Substitute Executor'}
          >
            {substitutesList.modal.isOpen && substitutesList.modal.data && (
              <PersonForm
                initialData={substitutesList.modal.data}
                onSave={substitutesList.modal.save}
                showProfessionalOption={true}
                showEmail={true}
                showAgeSelection={true}
                hideUnder18={true}
              />
            )}
          </Modal>

          {/* Quick Add Person Edit Modal */}
          <Modal
            isOpen={editingQuickAddPerson !== null && tempQuickAddPerson !== null}
            onClose={handleCloseQuickAddModal}
            title="Edit details"
          >
            {editingQuickAddPerson && tempQuickAddPerson && (
              <PersonForm
                initialData={tempQuickAddPerson}
                onSave={(data) => {
                  updatePerson(editingQuickAddPerson.fullName, {
                    fullName: data.fullName,
                    address: data.address,
                    isOver18: data.isOver18
                  });
                  handleCloseQuickAddModal();
                }}
                onDelete={manuallyAddedPeople.some((p: any) => p.fullName === editingQuickAddPerson.fullName) ? () => {
                  // Remove from executors list if selected
                  const executorIndex = fields.findIndex(f => f.fullName === editingQuickAddPerson.fullName);
                  if (executorIndex !== -1) {
                    executorsList.remove(executorIndex);
                  }

                  // Remove from substitute executors list if selected
                  const subExecutorIndex = subFields.findIndex(f => f.fullName === editingQuickAddPerson.fullName);
                  if (subExecutorIndex !== -1) {
                    substitutesList.remove(subExecutorIndex);
                  }

                  removePersonEverywhere(editingQuickAddPerson.fullName);
                  handleCloseQuickAddModal();
                } : undefined}
                submitLabel="Save"
                showAgeSelection={true}
                hideUnder18={true}
              />
            )}
          </Modal>

          {/* Single Executor Warning Modal */}
          <Modal
            isOpen={showSingleExecutorWarning}
            onClose={() => setShowSingleExecutorWarning(false)}
            title="Are you sure you want only one Executor?"
          >
            <div className="space-y-6">
              <p className="text-gray-600 text-base leading-relaxed">
                It is good practice to appoint two or more executors, but if you only want to appoint one then that is legally fine.
              </p>

              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setHasConfirmedSingle(true);
                    handleConfirmSingle();
                  }}
                  className="w-full py-4 px-6 rounded-xl border-2 transition-all font-medium text-base text-left border-gray-200 hover:border-primary hover:bg-primary/5 bg-white group"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-6 w-6 rounded border-2 flex items-center justify-center border-gray-300 bg-white group-hover:border-primary" />
                    <span className="text-gray-700 font-medium">Yes, continue with one</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSingleExecutorWarning(false);
                  }}
                  className="w-full py-4 px-6 rounded-xl border-2 transition-all font-medium text-base text-left border-gray-200 hover:border-primary hover:bg-primary/5 bg-white group"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-6 w-6 rounded border-2 flex items-center justify-center border-gray-300 bg-white group-hover:border-primary" />
                    <span className="text-gray-700 font-medium">No, I&apos;ll add another</span>
                  </div>
                </button>
              </div>
            </div>
          </Modal>
        </form>
      </StepWrapper>
    </>
  );
};
